import {Component, computed, input, OnInit, signal, WritableSignal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {takeUntil} from 'rxjs';
import {FieldWrapperComponent} from '@shared/field/field-wrapper/field-wrapper.component';
import {AbstractFieldComponent} from '@shared/field/abstract-field.component';

@Component({
  selector: 'app-integer-range',
  imports: [
    ReactiveFormsModule,
    FieldWrapperComponent
  ],
  templateUrl: './integer-range.component.html',
  styleUrls: [
    './integer-range.component.scss'
  ]
})
export class IntegerRangeComponent
  extends AbstractFieldComponent<FormGroup<{min: FormControl<number>, max: FormControl<number>}>>
  implements OnInit {

  public readonly showValueLabel = input<boolean>(false);
  public readonly disableBounds = input<boolean>(true);

  private readonly TRACK_MARGIN = 0.5;

  public activeHandle = signal<'min' | 'max' | null>(null);

  public minValue: WritableSignal<number>;
  public maxValue: WritableSignal<number>;

  public minHandleLeft = computed(() => (this.minValue() ?? this.minBound()));
  public maxHandleRight = computed(() => (100 - (this.maxValue() ?? this.maxBound())));
  public trackLeft = computed(
    () =>((this.minValue() + this.TRACK_MARGIN) * 100 / (this.maxBound() - this.minBound()))
  );
  public trackWidth = computed(
    () => ((this.maxValue() - 2 * this.TRACK_MARGIN) - this.minValue()) * 100 / (this.maxBound() - this.minBound())
  );
  public minLabel = computed(() => {
    const min = this.minValue();
    const bound = this.minBound();
    if (min > bound || !this.disableBounds()) {
      return min;
    }
    return '-';
  });
  public maxLabel = computed(() => {
    const max = this.maxValue();
    const bound = this.maxBound();
    if (max < bound || !this.disableBounds()) {
      return max;
    }
    return '-';
  });

  public minBound = input<number>(0);
  public maxBound = input<number>(100);

  public handleControl = new FormControl<number>(0, {nonNullable: true});

  public ngOnInit(): void {
    this.minValue = signal(this.typedControl().controls.min.value);
    this.maxValue = signal(this.typedControl().controls.max.value);
    this.typedControl().controls.min.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(val => {
      this.minValue.set(val);
    });
    this.typedControl().controls.max.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(val => {
      this.maxValue.set(val);
    });
  }

  public onHandleInput(event: Event) {
    const value = this.handleControl.value;
    this.selectHandle();
    const handle = this.activeHandle();
    const boundedVal = this.boundValue(value);
    const control = handle === 'max'
      ? this.typedControl().controls.max
      : this.typedControl().controls.min;
    this.setControlValue(control, boundedVal);
    this.activeHandle.set(null);
    event.preventDefault();
    event.stopPropagation();
  }

  private selectHandle(): void {
    if (this.activeHandle() !== null) {
      return;
    }
    const pointerValue = this.handleControl.value;
    const min = this.minValue() ?? this.minBound();
    const max = this.maxValue() ?? this.maxBound();
    if (pointerValue <= min) {
      this.activeHandle.set('min');
      return;
    }
    if (pointerValue >= max) {
      this.activeHandle.set('max');
      return;
    }
    const maxDist = Math.abs(max - pointerValue);
    const minDist = Math.abs(min - pointerValue);
    if (maxDist < minDist) {
      this.activeHandle.set('max');
      return;
    }
    this.activeHandle.set('min');
  }

  private boundValue(value: number): number {
    const handle = this.activeHandle();
    const minDist = 1;
    const minVal = this.minValue();
    const realMin = handle === 'min' || minVal === undefined
      ? this.minBound()
      : (minVal + minDist);
    if (value < realMin) {
      return realMin;
    }
    const maxVal = this.maxValue();
    const realMax = handle === 'max' || maxVal === undefined
      ? this.maxBound()
      : (maxVal - minDist);
    if (value > realMax) {
      return realMax;
    }
    return value;
  }

  private setControlValue(control: FormControl<number>, val: number): void {
    control.patchValue(val);
    control.markAsDirty();
    control.markAsTouched();
  }
}
